import type { IconData } from "@loot-os/icons/types";
import { Button, type ButtonProps } from "../../core/Button.js";
import { buttonStyles } from "../../core/Button.styles.js";
import { Icon } from "../../core/Icon.js";
import { IconButton } from "../../core/IconButton.js";
import { iconButtonStyles } from "../../core/IconButton.styles.js";
import { AppLink } from "../../foundations/AppLink.js";
import type { ActionContent } from "./content.js";

export interface ActionButtonProps {
  action: ActionContent;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  fullWidth?: boolean;
  /**
   * Draws the action as this icon alone (a phone bar's deposit), named by its label. Takes
   * IconButton's look, so `variant` and `fullWidth` don't apply.
   */
  iconOnly?: IconData;
  /** A light sweeps across it as the pointer arrives (Button's `shine`); not on `iconOnly`. */
  shine?: boolean;
  className?: string;
}

/**
 * A content action drawn as a button: a link in button styles when it goes somewhere (`href` or
 * `render`), so it routes and opens in a new tab like any link; a Button when it only acts.
 */
export function ActionButton({
  action,
  variant,
  size,
  fullWidth,
  iconOnly,
  shine = false,
  className,
}: ActionButtonProps) {
  const links = action.href !== undefined || action.render !== undefined;
  if (iconOnly) {
    const iconSize = size === "sm" ? "sm" : "md";
    if (!links) {
      return (
        <IconButton
          icon={iconOnly}
          label={action.label}
          size={iconSize}
          onClick={action.onClick}
          className={className}
        />
      );
    }
    return (
      <AppLink
        href={action.href}
        render={action.render}
        onClick={action.onClick}
        aria-label={action.label}
        className={iconButtonStyles({ size: iconSize, className })}
      >
        <Icon icon={iconOnly} size={iconSize === "sm" ? 16 : 20} />
      </AppLink>
    );
  }
  if (links) {
    return (
      <AppLink
        href={action.href}
        render={action.render}
        onClick={action.onClick}
        className={buttonStyles({ variant, size, fullWidth, className })}
      >
        {action.label}
        {shine && <span aria-hidden className="shine-sweep" />}
      </AppLink>
    );
  }
  return (
    <Button
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      shine={shine}
      className={className}
      onClick={action.onClick}
    >
      {action.label}
    </Button>
  );
}
